import React, { useCallback, useEffect, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { puoGestire } from "@/lib/ruoli";
import {
  AlertTriangle, FileText, FileSpreadsheet, Plus, Lock, Unlock, Trash2, Edit,
} from "lucide-react";

/**
 * Registro infortuni e indici di frequenza e gravita'.
 *
 * Due cose che prima stavano separate: la scheda SC PR 4.4.7.2.1 compilata a
 * mano, una per evento, e il foglio Excel dove i numeri dell'anno venivano
 * ricopiati per calcolare gli indici. Qui l'evento si registra una volta e
 * gli indici si contano da soli.
 */
const giorno = (iso) => (iso || "").substring(0, 10).split("-").reverse().join("/");
const numero = (v, cifre = 2) =>
  v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT",
    { minimumFractionDigits: cifre, maximumFractionDigits: cifre });

const COLORE_GENERE = {
  infortunio: "bg-red-100 text-red-800",
  incidente: "bg-amber-100 text-amber-800",
  quasi_incidente: "bg-slate-100 text-slate-700",
};

export default function Infortuni() {
  const { user } = useAuth();
  const gestisce = puoGestire(user);
  const [elenchi, setElenchi] = useState(null);
  const [eventi, setEventi] = useState([]);
  const [indici, setIndici] = useState(null);
  const [ripartizioni, setRipartizioni] = useState(null);
  const [modulo, setModulo] = useState(null);
  const [annoDa, setAnnoDa] = useState(null);
  const [prospetto, setProspetto] = useState(null);
  const [cella, setCella] = useState(null);

  const carica = useCallback(async () => {
    try {
      const [e, i, r, p] = await Promise.all([
        api.get("/infortuni"),
        api.get("/infortuni/indici/riepilogo"),
        api.get("/infortuni/indici/ripartizioni"),
        api.get("/indicatori/prospetto"),
      ]);
      setEventi(e.data);
      setIndici(i.data);
      setRipartizioni(r.data);
      setProspetto(p.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    }
  }, []);

  useEffect(() => {
    api.get("/infortuni/elenchi").then(({ data }) => setElenchi(data)).catch(() => {});
    carica();
  }, [carica]);

  const apriScheda = async (ev) => {
    try {
      const r = await authFetch(`/infortuni/${ev.id}/scheda-pdf`);
      if (!r.ok) throw new Error((await r.json()).detail || "Errore");
      const u = window.URL.createObjectURL(await r.blob());
      window.open(u, "_blank");
    } catch (err) { toast.error(err.message || "Errore"); }
  };

  const scaricaIndici = async () => {
    try {
      const r = await authFetch("/infortuni/indici/excel");
      if (!r.ok) throw new Error("Errore");
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = "indici_infortuni_SGM.xlsx"; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download"); }
  };

  const scaricaProspetto = async () => {
    try {
      const r = await authFetch("/indicatori/prospetto/excel");
      if (!r.ok) throw new Error("Errore");
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = "prospetto_indicatori_SGM.xlsx"; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download"); }
  };

  const cambiaStato = async (ev, azione) => {
    try {
      await api.post(`/infortuni/${ev.id}/${azione}`);
      toast.success(azione === "chiudi" ? "Indagine chiusa" : "Riaperto");
      carica();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const elimina = async (ev) => {
    if (!window.confirm(
      `Cancellare il rapporto ${ev.numero}/${ev.anno}? Il numero resta saltato: `
      + "un registro infortuni con i progressivi rifatti non vale.")) return;
    try {
      await api.delete(`/infortuni/${ev.id}`);
      toast.success("Registrazione cancellata");
      carica();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const aperti = eventi.filter((e) => e.stato !== "chiuso").length;

  return (
    <div className="space-y-4" data-testid="pagina-infortuni">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl">Infortuni e incidenti</h1>
          <p className="text-sm text-slate-500">
            Il registro degli eventi e gli indici di frequenza e gravità.
            {aperti > 0 && <> · <strong className="text-amber-700">{aperti} con l&apos;indagine aperta</strong></>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-11" onClick={scaricaIndici}
                  data-testid="indici-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Indici in Excel
          </Button>
          {gestisce && (
            <Button className="h-11 bg-orange-500 hover:bg-orange-600"
                    onClick={() => setModulo({})} data-testid="nuovo-infortunio-btn">
              <Plus className="w-4 h-4 mr-2" /> Registra evento
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="registro">
        <TabsList>
          <TabsTrigger value="registro" data-testid="tab-registro">
            Registro ({eventi.length})
          </TabsTrigger>
          <TabsTrigger value="indici" data-testid="tab-indici">Indici</TabsTrigger>
          <TabsTrigger value="prospetto" data-testid="tab-prospetto">
            Prospetto indicatori
          </TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------ il registro --- */}
        <TabsContent value="registro" className="space-y-2">
          {eventi.length === 0 && (
            <p className="text-sm text-slate-500">
              Nessun evento registrato. È la notizia migliore della giornata.
            </p>
          )}
          {eventi.map((ev) => (
            <Card key={ev.id} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-slate-500">
                        Rapporto {ev.numero}/{ev.anno}
                      </span>
                      <Badge className={`${COLORE_GENERE[ev.genere] || ""} border-0`}>
                        {ev.genere_nome}
                      </Badge>
                      {ev.stato === "chiuso"
                        ? <Badge variant="outline" className="text-[10px]">Indagine chiusa</Badge>
                        : <Badge className="bg-amber-500 text-white border-0 text-[10px]">
                            Indagine aperta
                          </Badge>}
                      {ev.giorni_prognosi > 0 && (
                        <Badge variant="outline" className="text-[10px]">
                          {ev.giorni_prognosi} giorni di prognosi
                        </Badge>
                      )}
                    </div>
                    <div className="font-semibold mt-1">
                      {ev.infortunato || "—"}
                      <span className="font-normal text-slate-500 text-sm">
                        {" · "}{giorno(ev.data)}{ev.ora ? ` ore ${ev.ora}` : ""}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {[ev.luoghi_nome?.join(", "), ev.reparto_nome,
                        ev.parte_del_corpo].filter(Boolean).join(" · ")}
                    </div>
                    {ev.descrizione && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{ev.descrizione}</p>
                    )}
                    {(ev.cosa_e_successo_nomi?.length > 0 || ev.natura_lesione_nomi?.length > 0) && (
                      <p className="text-xs text-slate-500 mt-1">
                        {[...(ev.cosa_e_successo_nomi || []),
                          ...(ev.natura_lesione_nomi || [])].join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => apriScheda(ev)}
                            data-testid={`scheda-${ev.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> Scheda
                    </Button>
                    {gestisce && (<>
                      <Button variant="outline" size="sm" onClick={() => setModulo(ev)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm"
                              onClick={() => cambiaStato(ev, ev.stato === "chiuso" ? "riapri" : "chiudi")}>
                        {ev.stato === "chiuso"
                          ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => elimina(ev)}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* -------------------------------------------------- gli indici --- */}
        <TabsContent value="indici" className="space-y-3">
          {indici?.avviso_ore && (
            <p className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-3">
              <AlertTriangle className="w-4 h-4 inline mr-1 -mt-0.5" />
              {indici.avviso_ore}
            </p>
          )}
          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-2">Anno</th>
                    <th className="text-right p-2">Ore lavorate</th>
                    <th className="text-right p-2">Infortuni</th>
                    <th className="text-right p-2">Giorni</th>
                    <th className="text-right p-2">Addetti</th>
                    <th className="text-right p-2">Indice frequenza</th>
                    <th className="text-right p-2">Indice gravità</th>
                    <th className="text-right p-2">Durata media</th>
                    <th className="text-right p-2">Tasso incidenza</th>
                    <th className="text-right p-2">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {(indici?.righe || []).map((r) => (
                    <tr key={r.anno} className="border-t border-slate-100">
                      <td className="p-2 font-semibold">{r.anno}</td>
                      <td className="p-2 text-right tabular-nums">
                        {r.ore ? numero(r.ore, 0) : "—"}
                        <span className={`block text-[10px] ${
                          r.ore_origine === "dichiarate" ? "text-emerald-700" : "text-amber-700"}`}>
                          {r.ore_origine === "dichiarate" ? "da busta paga"
                            : r.ore_origine === "stimate" ? "stima" : "da inserire"}
                        </span>
                      </td>
                      <td className="p-2 text-right tabular-nums">{r.infortuni}</td>
                      <td className="p-2 text-right tabular-nums">{r.giorni}</td>
                      <td className="p-2 text-right tabular-nums">{r.n_dipendenti ?? "—"}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{numero(r.frequenza)}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{numero(r.gravita)}</td>
                      <td className="p-2 text-right tabular-nums">{numero(r.durata_media)}</td>
                      <td className="p-2 text-right tabular-nums">{numero(r.tasso_incidenza, 3)}</td>
                      <td className="p-2 text-right">
                        {gestisce && (
                          <Button variant="outline" size="sm" className="h-7 text-xs"
                                  data-testid={`anno-${r.anno}`}
                                  onClick={() => setAnnoDa(r)}>
                            ore e addetti
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <p className="text-xs text-slate-500">
            {Object.entries(indici?.formule || {}).map(([k, v]) => (
              <span key={k} className="block">
                <strong>{k.replace(/_/g, " ")}</strong>: {v}
              </span>
            ))}
          </p>

          {ripartizioni && ripartizioni.totale > 0 && (
            <div className="grid md:grid-cols-3 gap-3">
              <Riquadro titolo="Per reparto" voci={ripartizioni.per_reparto}
                        totale={ripartizioni.totale} />
              <Riquadro titolo="Per sesso" voci={ripartizioni.per_sesso}
                        totale={ripartizioni.totale} />
              <Card className="border-slate-200">
                <CardContent className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    Parte del corpo
                  </div>
                  {ripartizioni.per_parte_del_corpo.filter((p) => p.numero > 0).map((p) => (
                    <div key={p.parte} className="flex justify-between text-sm">
                      <span>{p.parte}</span>
                      <span className="tabular-nums text-slate-500">
                        {p.numero} · {(p.quota * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                  {ripartizioni.per_parte_del_corpo.every((p) => !p.numero) && (
                    <p className="text-sm text-slate-400">Nessun dato</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ------------------------------ il prospetto TAB PR 4.5.1.2.1 --- */}
        <TabsContent value="prospetto" className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm text-slate-500">
              È il prospetto TAB PR 4.5.1.2.1. Quello che l&apos;app sa lo conta
              da sola e si aggiorna da solo; il resto lo scrivi tu una volta
              l&apos;anno.
            </p>
            <Button variant="outline" size="sm" onClick={scaricaProspetto}
                    data-testid="prospetto-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Prospetto in Excel
            </Button>
          </div>
          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-2">Indicatore</th>
                    {(prospetto?.anni || []).map((a) => (
                      <th key={a} className="text-right p-2">{a}</th>
                    ))}
                    <th className="text-right p-2">Obiettivo</th>
                  </tr>
                </thead>
                <tbody>
                  {(prospetto?.righe || []).map((riga, i) => {
                    const primo = i === 0
                      || prospetto.righe[i - 1].gruppo !== riga.gruppo;
                    return (
                      <React.Fragment key={riga.codice}>
                        {primo && (
                          <tr className="bg-slate-50">
                            <td colSpan={(prospetto.anni?.length || 0) + 2}
                                className="p-1 px-2 text-[10px] uppercase tracking-wider text-[#0067b2] font-semibold">
                              {riga.gruppo}
                            </td>
                          </tr>
                        )}
                        <tr className="border-t border-slate-100 align-top">
                          <td className="p-2">
                            <div className="font-medium">{riga.nome}</div>
                            <div className="text-[11px] text-slate-500">
                              <span className="font-mono">{riga.codice}</span> · {riga.formula}
                            </div>
                          </td>
                          {(prospetto.anni || []).map((a) => {
                            const v = riga.anni[a] || {};
                            const quoziente = v.numeratore === null || v.numeratore === undefined
                              ? "" : `${v.numeratore} / ${v.denominatore ?? "—"}`;
                            return (
                              <td key={a} className="p-2 text-right tabular-nums">
                                <button type="button" disabled={!gestisce} title={quoziente}
                                        onClick={() => setCella({ riga, anno: a, valore: v })}
                                        className={`w-full text-right ${gestisce ? "hover:underline" : "cursor-default"}`}>
                                  {v.valore === null || v.valore === undefined
                                    ? <span className="text-slate-300">—</span>
                                    : numero(v.valore, riga.cifre)}
                                  <span className={`block text-[9px] ${
                                    v.fonte === "calcolato" ? "text-emerald-600"
                                      : v.fonte === "a mano" ? "text-slate-400"
                                        : "text-amber-600"}`}>
                                    {v.fonte}
                                  </span>
                                </button>
                              </td>
                            );
                          })}
                          <td className="p-2 text-right text-[11px] text-slate-500">
                            {riga.obiettivo || "—"}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <p className="text-xs text-slate-500">
            <strong className="text-emerald-700">calcolato</strong>: lo conta
            l&apos;app dai suoi dati · <strong>a mano</strong>: scritto da una
            persona · <strong className="text-amber-700">da scrivere</strong>:
            manca. Passa il mouse su un numero calcolato per vedere i due
            termini da cui esce.
          </p>
        </TabsContent>
      </Tabs>

      {modulo && elenchi && (
        <ModuloEvento iniziale={modulo} elenchi={elenchi}
                      onChiudi={() => setModulo(null)}
                      onSalvato={() => { setModulo(null); carica(); }} />
      )}
      {annoDa && (
        <ModuloAnno riga={annoDa} onChiudi={() => setAnnoDa(null)}
                    onSalvato={() => { setAnnoDa(null); carica(); }} />
      )}
      {cella && (
        <ModuloValore cella={cella} onChiudi={() => setCella(null)}
                      onSalvato={() => { setCella(null); carica(); }} />
      )}
    </div>
  );
}

/**
 * Il valore di un indicatore che l'app non sa calcolare.
 *
 * Vale anche per correggere un indicatore calcolato: gli anni prima
 * dell'app non hanno i dati dentro, e il numero vero lo sa solo chi c'era.
 */
function ModuloValore({ cella, onChiudi, onSalvato }) {
  const { riga, anno, valore } = cella;
  const [v, setV] = useState(valore.fonte === "a mano" ? String(valore.valore ?? "") : "");
  const [note, setNote] = useState(valore.note || "");
  const [busy, setBusy] = useState(false);

  const salva = async (togli) => {
    setBusy(true);
    try {
      const corpo = togli ? {} : { valore: Number(v), note: note || null };
      await api.put(`/indicatori/valori/${anno}/${riga.codice}`, corpo);
      toast.success(togli ? "Valore tolto" : `${riga.nome} ${anno} salvato`);
      onSalvato();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{riga.nome} · {anno}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            <span className="font-mono">{riga.codice}</span> · {riga.formula}
            {riga.obiettivo && <> · obiettivo: {riga.obiettivo}</>}
          </p>
          {!riga.manuale && valore.fonte === "calcolato" && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Questo indicatore l&apos;app lo calcola da sola
              ({numero(valore.valore, riga.cifre)}). Scrivendo un numero qui
              lo sostituisci: serve per gli anni in cui i dati nell&apos;app non
              ci sono. Per tornare al calcolo automatico, svuota e premi Togli.
            </p>
          )}
          <div>
            <Label>Valore</Label>
            <Input type="number" step="any" value={v} data-testid="valore-indicatore"
                   onChange={(e) => setV(e.target.value)} />
          </div>
          <div>
            <Label>Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="da dove viene il numero" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {valore.fonte === "a mano" && (
            <Button variant="outline" className="text-red-600 mr-auto" disabled={busy}
                    onClick={() => salva(true)}>Togli</Button>
          )}
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button className="bg-orange-500 hover:bg-orange-600" disabled={busy || v === ""}
                  onClick={() => salva(false)} data-testid="salva-valore">
            {busy ? "Salvo..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Riquadro({ titolo, voci, totale }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{titolo}</div>
        {Object.entries(voci || {}).map(([k, n]) => (
          <div key={k} className="flex justify-between text-sm">
            <span className="capitalize">{k}</span>
            <span className="tabular-nums text-slate-500">
              {n} · {totale ? ((n / totale) * 100).toFixed(1) : 0}%
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Le ore lavorate dell'anno e il numero di addetti.
 *
 * La stima dalla pianificazione si vede ma non si salva da sola: il numero
 * che conta e' quello della busta paga, e lo scrive una persona.
 */
function ModuloAnno({ riga, onChiudi, onSalvato }) {
  const [ore, setOre] = useState(riga.ore_origine === "dichiarate" ? String(riga.ore ?? "") : "");
  const [addetti, setAddetti] = useState(String(riga.n_dipendenti ?? ""));
  const [storicoInf, setStoricoInf] = useState(
    riga.da_registro ? "" : String(riga.infortuni ?? ""));
  const [storicoGg, setStoricoGg] = useState(
    riga.da_registro ? "" : String(riga.giorni ?? ""));
  const [busy, setBusy] = useState(false);

  const salva = async () => {
    setBusy(true);
    try {
      const corpo = {};
      if (ore !== "") corpo.ore = Number(ore);
      if (addetti !== "") corpo.n_dipendenti = Number(addetti);
      if (!riga.da_registro) {
        if (storicoInf !== "") corpo.storico_infortuni = Number(storicoInf);
        if (storicoGg !== "") corpo.storico_giorni = Number(storicoGg);
      }
      await api.put(`/infortuni/anni/${riga.anno}`, corpo);
      toast.success(`${riga.anno} aggiornato`);
      onSalvato();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Anno {riga.anno}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Ore lavorate</Label>
            <Input type="number" value={ore} onChange={(e) => setOre(e.target.value)}
                   data-testid="ore-anno" placeholder="dalla busta paga" />
            <p className="text-[11px] text-slate-500 mt-1">
              {riga.ore_origine === "stimate"
                ? <>La stima dalla pianificazione è <strong>{numero(riga.ore, 0)}</strong> ore.
                    Verifica il dato vero su Zucchetti e scrivilo qui: è quello che regge in audit.</>
                : <>Il numero della busta paga. Prendilo da Zucchetti, non dalla pianificazione.</>}
            </p>
          </div>
          <div>
            <Label>Numero di dipendenti</Label>
            <Input type="number" value={addetti} data-testid="addetti-anno"
                   onChange={(e) => setAddetti(e.target.value)} />
          </div>
          {!riga.da_registro && (
            <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
              <div>
                <Label>Infortuni dell&apos;anno</Label>
                <Input type="number" value={storicoInf}
                       onChange={(e) => setStoricoInf(e.target.value)} />
              </div>
              <div>
                <Label>Giorni di inabilità</Label>
                <Input type="number" value={storicoGg}
                       onChange={(e) => setStoricoGg(e.target.value)} />
              </div>
              <p className="col-span-2 text-[11px] text-slate-500">
                Questi due numeri valgono solo finché l&apos;anno non ha eventi nel
                registro: appena ne registri uno, il conto lo fa l&apos;app.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button className="bg-orange-500 hover:bg-orange-600" disabled={busy}
                  onClick={salva} data-testid="salva-anno">
            {busy ? "Salvo..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * La scheda SC PR 4.4.7.2.1, compilata a schermo.
 *
 * Le tre parti A, B e C nello stesso ordine della carta, cosi' chi ha in mano
 * il modulo cartaceo ritrova i campi dove se li aspetta.
 */
function ModuloEvento({ iniziale, elenchi, onChiudi, onSalvato }) {
  const vuoto = {
    data: new Date().toISOString().substring(0, 10), ora: "",
    genere: "infortunio", luoghi: [], pronto_soccorso: false, descrizione: "",
    attivita_ordinaria: true, medico_in_azienda: false, senza_astensione: false,
    dpi: "si", cosa_e_successo: [], natura_lesione: [], parte_del_corpo: "",
    employee_id: "", infortunato: "", esterno: false, sesso: "uomo",
    reparto: "cantiere", altre_persone: [], luogo_descrizione: "",
    attrezzatura: "", impianto: "", area_altro: "",
    danno_a_cose: [], situazione_pericolosa: [], comportamento_pericoloso: [],
    cause: "", conseguenze: "", trattamento: "", intervista: "", conclusioni: "",
    azione_correttiva: false, azione_correttiva_testo: "", registro_n: "",
    giorni_prognosi: 0, certificato_successivo: "", note: "",
  };
  const [f, setF] = useState({ ...vuoto, ...iniziale });
  const [persone, setPersone] = useState([]);
  const [busy, setBusy] = useState(false);
  const modifica = Boolean(iniziale.id);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setPersone(data)).catch(() => {});
  }, []);

  const tocca = (campo, valore) => setF((p) => ({ ...p, [campo]: valore }));

  const spunta = (campo, chiave, acceso) => setF((p) => {
    const presenti = (p[campo] || []).filter((x) => x.chiave !== chiave);
    return { ...p, [campo]: acceso ? [...presenti, { chiave, dettaglio: "" }] : presenti };
  });
  const dettaglio = (campo, chiave, testo) => setF((p) => ({
    ...p,
    [campo]: (p[campo] || []).map((x) => (x.chiave === chiave ? { ...x, dettaglio: testo } : x)),
  }));
  const scelto = (campo, chiave) => (f[campo] || []).find((x) => x.chiave === chiave);

  const salva = async () => {
    setBusy(true);
    try {
      const corpo = { ...f, giorni_prognosi: Number(f.giorni_prognosi) || 0 };
      ["employee_id", "parte_del_corpo", "ora", "dpi", "reparto", "sesso"]
        .forEach((k) => { if (!corpo[k]) corpo[k] = null; });
      if (modifica) await api.put(`/infortuni/${iniziale.id}`, corpo);
      else await api.post("/infortuni", corpo);
      toast.success(modifica ? "Scheda aggiornata" : "Evento registrato");
      onSalvato();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally { setBusy(false); }
  };

  const Spunte = ({ titolo, campo, voci }) => (
    <div>
      <Label>{titolo}</Label>
      <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1 mt-1">
        {voci.map((v) => {
          const attiva = scelto(campo, v.chiave);
          return (
            <div key={v.chiave} className="flex items-center gap-2">
              <input type="checkbox" checked={Boolean(attiva)}
                     onChange={(e) => spunta(campo, v.chiave, e.target.checked)} />
              <span className="text-sm">{v.nome}</span>
              {attiva && v.dettaglio && (
                <Input className="h-7 text-xs flex-1" value={attiva.dettaglio || ""}
                       placeholder="che cosa"
                       onChange={(e) => dettaglio(campo, v.chiave, e.target.value)} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {modifica ? `Rapporto ${iniziale.numero}/${iniziale.anno}` : "Registra un evento"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            È la scheda SC PR 4.4.7.2.1, parti A, B e C: gli stessi campi del modulo
            di carta, nello stesso ordine. Quello che scrivi qui esce in PDF da firmare.
          </p>

          {/* ---------------------------------------------------- parte A -- */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Data *</Label>
              <Input type="date" value={f.data} data-testid="inf-data"
                     onChange={(e) => tocca("data", e.target.value)} /></div>
            <div><Label>Ora</Label>
              <Input type="time" value={f.ora || ""}
                     onChange={(e) => tocca("ora", e.target.value)} /></div>
            <div><Label>Tipo di evento</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3"
                      value={f.genere} data-testid="inf-genere"
                      onChange={(e) => tocca("genere", e.target.value)}>
                {Object.entries(elenchi.generi).map(([k, v]) =>
                  <option key={k} value={k}>{v}</option>)}
              </select></div>
          </div>

          <div className="flex flex-wrap gap-4">
            {Object.entries(elenchi.luoghi).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={(f.luoghi || []).includes(k)}
                       onChange={(e) => tocca("luoghi", e.target.checked
                         ? [...(f.luoghi || []), k]
                         : (f.luoghi || []).filter((x) => x !== k))} />
                {v}
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.pronto_soccorso}
                     onChange={(e) => tocca("pronto_soccorso", e.target.checked)} />
              Inviato al Pronto Soccorso
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Chi</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3"
                      value={f.employee_id || ""} data-testid="inf-persona"
                      onChange={(e) => tocca("employee_id", e.target.value)}>
                <option value="">— personale esterno o non in elenco —</option>
                {persone.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
              {!f.employee_id && (
                <Input className="mt-1" placeholder="Cognome e nome"
                       value={f.infortunato || ""}
                       onChange={(e) => tocca("infortunato", e.target.value)} />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Reparto</Label>
                <select className="w-full h-10 border border-slate-200 rounded px-3"
                        value={f.reparto || ""}
                        onChange={(e) => tocca("reparto", e.target.value)}>
                  <option value="">—</option>
                  {Object.entries(elenchi.reparti).map(([k, v]) =>
                    <option key={k} value={k}>{v}</option>)}
                </select></div>
              <div><Label>Sesso</Label>
                <select className="w-full h-10 border border-slate-200 rounded px-3"
                        value={f.sesso || ""}
                        onChange={(e) => tocca("sesso", e.target.value)}>
                  <option value="">—</option>
                  {Object.entries(elenchi.sessi).map(([k, v]) =>
                    <option key={k} value={k}>{v}</option>)}
                </select></div>
            </div>
          </div>

          <div>
            <Label>Descrizione dell&apos;accaduto (che lavoro stava svolgendo)</Label>
            <textarea className="w-full border border-slate-200 rounded p-2 text-sm" rows={3}
                      value={f.descrizione} data-testid="inf-descrizione"
                      onChange={(e) => tocca("descrizione", e.target.value)} />
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={Boolean(f.attivita_ordinaria)}
                     onChange={(e) => tocca("attivita_ordinaria", e.target.checked)} />
              Attività ordinaria
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={f.medico_in_azienda}
                     onChange={(e) => tocca("medico_in_azienda", e.target.checked)} />
              Medico in azienda
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={f.senza_astensione}
                     onChange={(e) => tocca("senza_astensione", e.target.checked)} />
              Lesione senza astensione dal lavoro
            </label>
            <div className="flex items-center gap-2">
              <span>Indossava i DPI:</span>
              <select className="h-8 border border-slate-200 rounded px-2"
                      value={f.dpi || ""} onChange={(e) => tocca("dpi", e.target.value)}>
                <option value="">—</option>
                {Object.entries(elenchi.dpi).map(([k, v]) =>
                  <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          <Spunte titolo="Che cosa è successo?" campo="cosa_e_successo"
                  voci={elenchi.cosa_e_successo} />
          <Spunte titolo="Che cosa si è fatto?" campo="natura_lesione"
                  voci={elenchi.natura_lesione} />

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Parte del corpo</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3"
                      value={f.parte_del_corpo || ""} data-testid="inf-parte"
                      onChange={(e) => tocca("parte_del_corpo", e.target.value)}>
                <option value="">—</option>
                {elenchi.parti_del_corpo.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></div>
            <div><Label>Sede / Cantiere / Luogo</Label>
              <Input value={f.luogo_descrizione || ""}
                     onChange={(e) => tocca("luogo_descrizione", e.target.value)} /></div>
          </div>

          {/* ---------------------------------------------------- parte B -- */}
          <div className="border-t border-slate-200 pt-3 space-y-3">
            <p className="text-xs uppercase tracking-wider text-slate-500">Parte B · analisi</p>
            <Spunte titolo="Incidente (danno a cose)" campo="danno_a_cose"
                    voci={elenchi.danno_a_cose} />
            <Spunte titolo="Situazione pericolosa" campo="situazione_pericolosa"
                    voci={elenchi.situazione_pericolosa} />
            <Spunte titolo="Comportamento pericoloso" campo="comportamento_pericoloso"
                    voci={elenchi.comportamento_pericoloso} />
            <div>
              <Label>Ipotesi sulle cause: perché è successo?</Label>
              <textarea className="w-full border border-slate-200 rounded p-2 text-sm" rows={2}
                        value={f.cause || ""} data-testid="inf-cause"
                        onChange={(e) => tocca("cause", e.target.value)} />
              <p className="text-[11px] text-slate-500 mt-1">
                Senza questa riga la scheda non si chiude: è il punto per cui esiste.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Conseguenze</Label>
                <Input value={f.conseguenze || ""}
                       onChange={(e) => tocca("conseguenze", e.target.value)} /></div>
              <div><Label>Trattamento posto in essere</Label>
                <Input value={f.trattamento || ""}
                       onChange={(e) => tocca("trattamento", e.target.value)} /></div>
            </div>
          </div>

          {/* ---------------------------------------------------- parte C -- */}
          <div className="border-t border-slate-200 pt-3 space-y-3">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Parte C · intervista e conclusioni
            </p>
            <div>
              <Label>Dichiarazioni dell&apos;infortunato</Label>
              <textarea className="w-full border border-slate-200 rounded p-2 text-sm" rows={2}
                        value={f.intervista || ""}
                        onChange={(e) => tocca("intervista", e.target.value)} />
            </div>
            <div>
              <Label>Conclusioni</Label>
              <textarea className="w-full border border-slate-200 rounded p-2 text-sm" rows={2}
                        value={f.conclusioni || ""}
                        onChange={(e) => tocca("conclusioni", e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.azione_correttiva}
                     onChange={(e) => tocca("azione_correttiva", e.target.checked)} />
              Azione correttiva e/o preventiva
            </label>
            {f.azione_correttiva && (
              <Input placeholder="Quale azione" value={f.azione_correttiva_testo || ""}
                     onChange={(e) => tocca("azione_correttiva_testo", e.target.value)} />
            )}
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>N. registro infortuni</Label>
                <Input value={f.registro_n || ""}
                       onChange={(e) => tocca("registro_n", e.target.value)} /></div>
              <div><Label>Giorni di prognosi</Label>
                <Input type="number" value={f.giorni_prognosi} data-testid="inf-prognosi"
                       onChange={(e) => tocca("giorni_prognosi", e.target.value)} /></div>
              <div><Label>Certificato successivo</Label>
                <Input value={f.certificato_successivo || ""}
                       onChange={(e) => tocca("certificato_successivo", e.target.value)} /></div>
            </div>
            <div>
              <Label>Note</Label>
              <Input value={f.note || ""} onChange={(e) => tocca("note", e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button className="bg-orange-500 hover:bg-orange-600" disabled={busy}
                  onClick={salva} data-testid="salva-infortunio">
            {busy ? "Salvo..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
